import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { auth, db } from './firebase';

const BACKEND_URL = 'https://pharmdev-rider.onrender.com';

async function authedFetch(path, body) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const inp = {
  width: '100%', padding: '10px 14px', borderRadius: 10,
  border: '1px solid #E5E7EB', fontSize: 13,
  backgroundColor: '#F7F8FA', outline: 'none', boxSizing: 'border-box',
};

// Every named pharmacist who'll ever verify a POM/controlled order needs to
// be here — PCG regulations require the person who signs off, not just "the
// pharmacy," so this list (and the PIN each one sets) is what makes the
// invoice/NEPP submission attributable to a real, registered pharmacist
// instead of just "whoever was logged into the shared dashboard."
export default function PharmacistsTab({ pharmacyId }) {
  const [pharmacists, setPharmacists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', pcgRegNo: '', pin: '', confirmPin: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!pharmacyId) return;
    return onValue(ref(db, `pharmacies/${pharmacyId}/pharmacists`), snap => {
      setPharmacists(snap.exists() ? Object.entries(snap.val()).map(([id, p]) => ({ id, ...p })) : []);
      setLoading(false);
    });
  }, [pharmacyId]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addPharmacist = async () => {
    if (!form.name.trim() || !form.pcgRegNo.trim()) {
      alert('Name and PCG registration number are required.');
      return;
    }
    if (!/^\d{4}$/.test(form.pin)) {
      alert('PIN must be exactly 4 digits.');
      return;
    }
    if (form.pin !== form.confirmPin) {
      alert('PINs do not match.');
      return;
    }
    setSaving(true);
    try {
      await authedFetch('/pharmacy/pharmacist/add', {
        name: form.name.trim(), pcgRegNo: form.pcgRegNo.trim(), pin: form.pin,
      });
      setForm({ name: '', pcgRegNo: '', pin: '', confirmPin: '' });
      setShowForm(false);
      alert('✅ Pharmacist added. They can now verify prescriptions with their PIN once PharmDev confirms their PCG registration.');
    } catch (e) {
      alert(e.message || 'Could not add pharmacist.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (pharmacist) => {
    await update(ref(db, `pharmacies/${pharmacyId}/pharmacists/${pharmacist.id}`), { active: !pharmacist.active });
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>;

  return (
    <div style={{ padding: 20, maxWidth: 700 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>🧑‍⚕️ Pharmacists</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            Each named pharmacist needs their own PIN to verify prescription/controlled orders — this is what identifies who signed off, for your invoices and NEPP submissions.
          </p>
        </div>
        <button
          style={{ padding: '10px 16px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          onClick={() => setShowForm(v => !v)}
        >
          {showForm ? 'Cancel' : '+ Add Pharmacist'}
        </button>
      </div>

      {showForm && (
        <div style={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, marginBottom: 20, display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Full Name</label>
            <input style={inp} value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. Ama Boateng" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>PCG Registration Number</label>
            <input style={inp} value={form.pcgRegNo} onChange={e => upd('pcgRegNo', e.target.value)} placeholder="Pharmacy Council of Ghana reg. no." />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>4-Digit PIN</label>
              <input style={inp} type="password" inputMode="numeric" maxLength={4} value={form.pin} onChange={e => upd('pin', e.target.value.replace(/\D/g, ''))} placeholder="••••" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Confirm PIN</label>
              <input style={inp} type="password" inputMode="numeric" maxLength={4} value={form.confirmPin} onChange={e => upd('confirmPin', e.target.value.replace(/\D/g, ''))} placeholder="••••" />
            </div>
          </div>
          <button
            style={{ padding: '11px', backgroundColor: saving ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14 }}
            onClick={addPharmacist}
            disabled={saving}
          >
            {saving ? 'Adding…' : '✓ Add Pharmacist'}
          </button>
        </div>
      )}

      {pharmacists.length === 0 ? (
        <p style={{ color: '#888', fontSize: 13 }}>No pharmacists added yet — add at least one before you can verify a prescription/controlled order.</p>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {pharmacists.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 14 }}>
              <div>
                <p style={{ fontWeight: 700, margin: 0, fontSize: 14 }}>{p.name}</p>
                <p style={{ fontSize: 12, color: '#888', margin: '2px 0 0' }}>PCG Reg: {p.pcgRegNo}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                  backgroundColor: p.verifiedByAdmin ? '#DCFCE7' : '#FEF3C7',
                  color: p.verifiedByAdmin ? '#15803D' : '#92400E',
                }}>
                  {p.verifiedByAdmin ? '✅ Verified' : '⏳ Awaiting PharmDev verification'}
                </span>
                <button
                  style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid #E5E7EB', backgroundColor: p.active ? '#FEE2E2' : '#F0FDF4', color: p.active ? '#B91C1C' : '#15803D', cursor: 'pointer' }}
                  onClick={() => toggleActive(p)}
                >
                  {p.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
